import { Button, Css, FilterDefs, Filters, ScrollableContent, usePersistedFilter } from "@homebound/beam";
import { useMemo } from "react";
import { createPlanPackageCreateUrl } from "src/RouteUrls";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, PlanPackageFilter } from "src/generated/graphqlTypes";
import { useMarketFilter, useReadyPlanStatusFilter } from "src/hooks";
import { PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import {
  PlanPackageFragment,
  usePlanPackagesQuery,
} from "src/routes/libraries/plan-package/list/PlanPackagesPage.generated";
import { planPackageCreateRoute } from "src/routing/domains/librariesPaths";
import { queryResult } from "src/utils";
import { PlanPackageCard } from "./PlanPackageCard";

export function PlanPackagesPage() {
  const isPlanPackagesV2Enabled = useFeatureFlag(FeatureFlagType.PlanPackagesV2);
  const { marketFilter: markets } = useMarketFilter();
  const { rpStatusFilter: status } = useReadyPlanStatusFilter();
  const filterDefs: FilterDefs<PlanPackageFilter> = useMemo(() => ({ markets, status }), [markets, status]);
  const { filter, setFilter } = usePersistedFilter<PlanPackageFilter>({
    filterDefs,
    storageKey: `plan-package-filter`,
  });
  const query = usePlanPackagesQuery({
    variables: { filter: { effectiveStatus: filter.status, markets: filter.markets } },
  });

  return (
    <div css={Css.h100.w100.$}>
      <PageHeader
        title="Plans"
        right={
          <Button
            label="Create"
            onClick={isPlanPackagesV2Enabled ? planPackageCreateRoute.url() : createPlanPackageCreateUrl()}
          />
        }
      />
      <TableActions onlyLeft>
        <h2 css={Css.lg.$}>All Plans</h2>
        <Filters<PlanPackageFilter> filter={filter} onChange={setFilter} filterDefs={filterDefs} />
      </TableActions>
      <ScrollableContent>
        {queryResult(query, ({ planPackages: { entities } }) => (
          <PlanPackageList plans={entities} />
        ))}
      </ScrollableContent>
    </div>
  );
}

type PlanPackageListProps = { plans: PlanPackageFragment[] };
function PlanPackageList(props: PlanPackageListProps) {
  const { plans } = props;
  return (
    <div css={Css.df.gap3.flexWrap("wrap").$}>
      {plans.length ? plans.map((plan) => <PlanPackageCard key={plan.id} planPackage={plan} />) : "No plans found"}
    </div>
  );
}
